import React, { CSSProperties, FC, PropsWithChildren } from 'react';
import { mergeClasses } from '@fluentui/react-components';
import { Alignment, IStack, IStackProps } from './types';
import {
  useHorizontalAlignmentStyles,
  useStackDirectionStyles,
  useStackStyles,
  useVerticalAlignmentStyles,
} from './styles';
import { Item } from './components/item';

const StackBase: FC<PropsWithChildren<IStackProps>> = (props) => {
  const { children, reversed, verticalAlign, horizontalAlign, horizontal, spacing, paddingInline, paddingBlock, wrap, verticalFill } = props;

  const classes = useStackStyles();
  const classesDirection = useStackDirectionStyles();

  const classesVerticalAlignment = useVerticalAlignmentStyles();
  const classesHorizontalAlignment = useHorizontalAlignmentStyles();

  // assign global styles
  const globalClass = mergeClasses(classes.container);

  // assign alignment styles
  const verticalAlignmentClass = classesVerticalAlignment[verticalAlign as Alignment];
  const horizontalAlignmentClass = classesHorizontalAlignment[horizontalAlign as Alignment];

  // assign wrap styles
  const wrapClass = wrap ? classes.wrap : classes.nowrap;

  // vertical fill
  const verticalFillClass = verticalFill ? classes.verticalFill : classes.verticalAuto;

  let directionClass = '';
  let reversedClass = '';
  let stackStyle: CSSProperties = {
    paddingBlock,
    paddingInline,
  };

  switch (horizontal) {
    case true:
      directionClass = classesDirection.horizontal;
      reversedClass = reversed ? classesDirection.horizontalReversed : '';
      if (spacing) stackStyle = { ...stackStyle, columnGap: spacing };
      if (wrap) stackStyle = { ...stackStyle, rowGap: spacing };
      break;
    case false:
    default:
      directionClass = classesDirection.vertical;
      reversedClass = reversed ? classesDirection.verticalReversed : '';
      if (spacing) stackStyle = { ...stackStyle, rowGap: spacing };
      if (wrap) stackStyle = { ...stackStyle, columnGap: spacing };
      break;
  }

  // merge classes
  const stackClass = mergeClasses(
    globalClass,
    wrapClass,
    directionClass,
    reversedClass,
    verticalFillClass,
    verticalAlignmentClass,
    horizontalAlignmentClass,
  );

  return <div className={stackClass} style={stackStyle && stackStyle}>{children && children}</div>;
};

export const defaultProps: IStackProps = {
  horizontal: false,
  reversed: false,
  spacing: '', // default spacing
  paddingInline: '', // default padding
  paddingBlock: '', // default padding
  horizontalAlign: 'start',
  verticalAlign: 'start',
};

// Export Stack component and include the Item component
export const Stack = StackBase as IStack;
Stack.defaultProps = defaultProps;
Stack.Item = Item;
Stack.displayName = 'Stack';
